import { match } from 'ts-pattern';

import { contentfulImageUrl } from '@telialt/utils/src/Contentful/contentfulImageUrl';

import { BlockQuickLinkType, QuickLinkType } from '../../models/Content';
import { transformCta } from '../../utils/transformers';
import { CtaData } from '../common/model';
import {
    BlockQuickLinkColorVariant,
    BlockQuickLinkData,
    BlockQuickLinkDisplayVariant,
    QuickLinkCardData,
} from './model';

const transformQuickLinkCard = (
    card: QuickLinkType,
    colorVariant: BlockQuickLinkColorVariant,
    displayVariant: BlockQuickLinkDisplayVariant,
    index: number,
): QuickLinkCardData => {
    if (!card) {
        return undefined;
    }

    const cardMediaUrl = contentfulImageUrl(card.fields.media?.fields.file?.url);
    const cardMediaAlt = card.fields.media?.fields.description;
    const cardMediaWidth = card.fields.media?.fields.file?.details?.image?.width;
    const cardMediaHeight = card.fields.media?.fields.file?.details?.image?.height;

    if (displayVariant === 'card-links') {
        return {
            id: `${card.sys.id}-${index}`,
            colorVariant,
            displayVariant,
            title: card.fields.title,
            iconImage: cardMediaUrl,
            icon: !cardMediaUrl ? card.fields.icon : undefined,
            links: card.fields.links?.map((childLink) => ({
                id: childLink.sys.id,
                title: childLink.fields.title,
                url: childLink.fields.url,
            })),
        };
    }

    if (displayVariant === 'card') {
        return {
            id: `${card.sys.id}-${index}`,
            colorVariant,
            displayVariant,
            title: card.fields.title,
            description: card.fields.description,
            url: card.fields.url,
            iconImage: cardMediaUrl,
            icon: !cardMediaUrl ? card.fields.icon : undefined,
            referencedContent: card.fields.referencedContent,
        };
    }

    if (displayVariant === 'card-image') {
        return {
            id: `${card.sys.id}-${index}`,
            colorVariant,
            displayVariant,
            title: card.fields.title,
            url: card.fields.url,
            image: cardMediaUrl
                ? { src: cardMediaUrl, alt: cardMediaAlt, width: cardMediaWidth, height: cardMediaHeight }
                : undefined,
            referencedContent: card.fields.referencedContent,
        };
    }

    return {
        id: `${card.sys.id}-${index}`,
        colorVariant,
        displayVariant,
        title: card.fields.title,
        url: card.fields.url,
        iconImage: cardMediaUrl,
        icon: !cardMediaUrl ? card.fields.icon : undefined,
        referencedContent: card.fields.referencedContent,
    };
};

export const transformBlockQuickLink = (blockQuickLink: BlockQuickLinkType): BlockQuickLinkData => {
    if (!blockQuickLink) {
        return undefined;
    }

    const colorVariant = match<BlockQuickLinkType['fields']['colorOption'], BlockQuickLinkColorVariant>(
        blockQuickLink.fields.colorOption,
    )
        .with('secondary', () => 'secondary')
        .otherwise(() => 'primary');

    const displayVariant = match<BlockQuickLinkType['fields']['displayOption'], BlockQuickLinkDisplayVariant>(
        blockQuickLink.fields.displayOption,
    )
        .with('card', () => 'card')
        .with('card with links', () => 'card-links')
        .with('card with image', () => 'card-image')
        .otherwise(() => 'link');

    return {
        id: blockQuickLink.sys.id,
        displayVariant,
        blockTitle: blockQuickLink.fields.title,
        blockCta: transformCta(blockQuickLink.fields.cta),
        cards: blockQuickLink.fields.quickLinks?.map((card, index) =>
            transformQuickLinkCard(card, colorVariant, displayVariant, index),
        ),
    };
};

export const transformQuickLinkCardToCta = (card: QuickLinkCardData): CtaData => {
    return {
        id: card.id,
        title: card.title,
        url: card.url,
        referencedContent: card.referencedContent,
    };
};
